/**
 * AiStatusPanel — admin view of the Universal-Key-backed LLM health.
 *
 * Polls GET /api/admin/ai-status every 8 seconds. Surfaces:
 *   - success rate (last 30 calls) with a Healthy / Degraded / At-risk pill
 *   - counters: successes / retries / failures
 *   - "Run health check" button → POST /api/admin/ai-status/test
 *   - top-up reminder for the Universal Key
 *   - call log (last 20 attempts, colour-coded by status)
 *
 * Pattern matches the sibling SmartNote2Self License Risk Scan app so future
 * copy-paste between Emergent apps stays trivial. data-testid attributes are
 * kept stable across both apps.
 */
import { useEffect, useState, useCallback } from "react";
import {
  CheckCircle2,
  XCircle,
  RefreshCw,
  AlertTriangle,
  Activity,
  Zap,
} from "lucide-react";
import { api } from "@/lib/api";

const POLL_INTERVAL_MS = 8000;

function PillBadge({ status }) {
  const styles = {
    healthy:  "bg-emerald-100 text-emerald-800 dark:bg-emerald-500/15 dark:text-emerald-300",
    degraded: "bg-amber-100 text-amber-900 dark:bg-amber-500/15 dark:text-amber-300",
    atrisk:   "bg-rose-100 text-rose-900 dark:bg-rose-500/15 dark:text-rose-300",
    unknown:  "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300",
  };
  const label = {
    healthy: "Healthy",
    degraded: "Degraded",
    atrisk: "At risk",
    unknown: "No data",
  }[status];
  return (
    <span
      data-testid="ai-status-health-pill"
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-medium ${styles[status]}`}
    >
      <span className="w-1.5 h-1.5 rounded-full bg-current opacity-70" />
      {label}
    </span>
  );
}

function StatCard({ label, value, testId, tone = "slate" }) {
  const tones = {
    slate:   "text-slate-900 dark:text-slate-50",
    emerald: "text-emerald-700 dark:text-emerald-400",
    amber:   "text-amber-700 dark:text-amber-400",
    rose:    "text-rose-700 dark:text-rose-400",
  };
  return (
    <div
      data-testid={testId}
      className="rounded-lg border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-5"
    >
      <p className="text-[11px] uppercase tracking-[0.14em] text-slate-500 dark:text-slate-400">
        {label}
      </p>
      <p className={`mt-2 font-display text-3xl font-semibold tracking-tight ${tones[tone]}`}>
        {value}
      </p>
    </div>
  );
}

function LogRow({ row }) {
  const status = row.status || "unknown";
  const rowTone = {
    success: "border-l-emerald-500",
    retry:   "border-l-amber-500",
    failed:  "border-l-rose-500",
  }[status] || "border-l-slate-300";
  const Icon = status === "success" ? CheckCircle2 : status === "retry" ? RefreshCw : XCircle;
  const iconClass = {
    success: "text-emerald-600 dark:text-emerald-400",
    retry:   "text-amber-600 dark:text-amber-400",
    failed:  "text-rose-600 dark:text-rose-400",
  }[status] || "text-slate-400";

  return (
    <tr
      data-testid="ai-status-log-row"
      className={`border-l-4 ${rowTone} border-b border-slate-100 dark:border-slate-800`}
    >
      <td className="px-3 py-2.5 align-top">
        <div className="flex items-center gap-2">
          <Icon className={`h-4 w-4 shrink-0 ${iconClass}`} />
          <span className="font-mono text-xs text-slate-700 dark:text-slate-200 capitalize">
            {status}
          </span>
        </div>
      </td>
      <td className="px-3 py-2.5 text-xs text-slate-600 dark:text-slate-300">
        {row.purpose || "—"}
      </td>
      <td className="px-3 py-2.5 text-xs text-slate-600 dark:text-slate-300">
        {row.attempt}/{row.max_attempts}
      </td>
      <td className="px-3 py-2.5 text-xs text-slate-600 dark:text-slate-300">
        {row.latency_ms != null ? `${row.latency_ms} ms` : "—"}
      </td>
      <td className="px-3 py-2.5 text-xs text-slate-500 dark:text-slate-400 font-mono">
        {row.ts ? new Date(row.ts).toLocaleString() : "—"}
      </td>
      <td className="px-3 py-2.5 text-xs text-slate-500 dark:text-slate-400 max-w-md truncate">
        {row.error || row.assessment_id || ""}
      </td>
    </tr>
  );
}

export default function AiStatusPanel() {
  const [status, setStatus] = useState(null);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [loadError, setLoadError] = useState(null);

  const fetchStatus = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/ai-status");
      setStatus(data);
      setLoadError(null);
    } catch (e) {
      setLoadError(e?.response?.data?.detail || e?.message || "Failed to load AI status");
    }
  }, []);

  useEffect(() => {
    fetchStatus();
    const t = setInterval(fetchStatus, POLL_INTERVAL_MS);
    return () => clearInterval(t);
  }, [fetchStatus]);

  const runHealthCheck = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const { data } = await api.post("/admin/ai-status/test");
      setTestResult(data);
      fetchStatus();
    } catch (e) {
      setTestResult({
        ok: false,
        error: e?.response?.data?.detail || e?.message || "Health check failed",
      });
    } finally {
      setTesting(false);
    }
  };

  const successRate = status?.success_rate_pct;
  const health =
    successRate == null
      ? "unknown"
      : successRate >= 95
        ? "healthy"
        : successRate >= 70
          ? "degraded"
          : "atrisk";

  const counts = status?.counts_last_30 || { success: 0, retry: 0, failed: 0 };

  return (
    <div data-testid="ai-status-panel" className="space-y-6">
      {/* Header row */}
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <p className="text-[11px] uppercase tracking-[0.18em] text-teal-700 dark:text-teal-400">
            AI Status
          </p>
          <h2 className="mt-1 font-display text-2xl font-semibold text-slate-900 dark:text-slate-50 tracking-tight">
            Universal Key health
          </h2>
          <p className="mt-1 text-sm text-slate-600 dark:text-slate-300">
            Last 30 LLM attempts ·{" "}
            <span className="font-mono text-xs">{status?.model || "claude-sonnet-4-5"}</span>
            {" · auto-refreshes every 8s"}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <PillBadge status={health} />
          <button
            data-testid="ai-status-test-button"
            onClick={runHealthCheck}
            disabled={testing}
            className="inline-flex items-center gap-2 h-10 px-4 rounded-md bg-slate-900 dark:bg-teal-600 text-white text-sm font-medium hover:bg-slate-800 dark:hover:bg-teal-500 transition-colors disabled:opacity-50"
          >
            {testing ? (
              <>
                <RefreshCw className="h-4 w-4 animate-spin" /> Testing…
              </>
            ) : (
              <>
                <Zap className="h-4 w-4" /> Run health check
              </>
            )}
          </button>
        </div>
      </div>

      {/* Health-check inline result */}
      {testResult && (
        <div
          data-testid="ai-status-test-result"
          className={`rounded-lg border p-4 ${
            testResult.ok
              ? "border-emerald-300 bg-emerald-50 dark:border-emerald-500/30 dark:bg-emerald-500/10"
              : "border-rose-300 bg-rose-50 dark:border-rose-500/30 dark:bg-rose-500/10"
          }`}
        >
          <div className="flex items-start gap-3">
            {testResult.ok ? (
              <CheckCircle2 className="h-5 w-5 text-emerald-600 dark:text-emerald-400 mt-0.5 shrink-0" />
            ) : (
              <XCircle className="h-5 w-5 text-rose-600 dark:text-rose-400 mt-0.5 shrink-0" />
            )}
            <div className="text-sm">
              {testResult.ok ? (
                <p className="text-emerald-900 dark:text-emerald-200">
                  Reply: <strong className="font-mono">{testResult.reply}</strong>
                  {" · "}
                  <span className="text-xs">~{testResult.latency_ms} ms</span>
                </p>
              ) : (
                <p className="text-rose-900 dark:text-rose-200 break-words font-mono text-xs">
                  {testResult.error}
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {loadError && (
        <div className="rounded-lg border border-rose-300 bg-rose-50 p-4 text-sm text-rose-900">
          {loadError}
        </div>
      )}

      {/* Counter cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard
          testId="ai-status-success-rate"
          label="Success rate"
          value={successRate == null ? "—" : `${successRate}%`}
          tone={health === "healthy" ? "emerald" : health === "degraded" ? "amber" : "rose"}
        />
        <StatCard
          testId="ai-status-successes"
          label="Successes"
          value={counts.success}
          tone="emerald"
        />
        <StatCard
          testId="ai-status-retries"
          label="Retries"
          value={counts.retry}
          tone="amber"
        />
        <StatCard
          testId="ai-status-failures"
          label="Failures"
          value={counts.failed}
          tone="rose"
        />
      </div>

      {/* Universal Key top-up reminder */}
      <div className="rounded-lg border border-amber-300 dark:border-amber-500/30 bg-amber-50 dark:bg-amber-500/10 p-5">
        <div className="flex items-start gap-3">
          <AlertTriangle className="h-5 w-5 text-amber-600 dark:text-amber-400 mt-0.5 shrink-0" />
          <div>
            <p className="font-display font-semibold text-slate-900 dark:text-slate-50">
              Universal Key balance
            </p>
            <p className="mt-1.5 text-sm text-amber-900 dark:text-amber-200 leading-relaxed">
              {status?.topup_reminder ||
                "Top up via Emergent platform → Profile → Universal Key → Add Balance."}
            </p>
            <ol className="mt-3 text-xs text-amber-900 dark:text-amber-200 space-y-1 list-decimal list-inside">
              <li>Emergent platform → Profile icon</li>
              <li>Universal Key → Add Balance (or enable Auto Top-up)</li>
              <li>If credits are healthy but calls fail, contact{" "}
                <a className="underline" href="mailto:support@emergent.sh">
                  support@emergent.sh
                </a>{" "}
                to raise the key's <code>max_budget</code> cap
              </li>
            </ol>
          </div>
        </div>
      </div>

      {/* Call log */}
      <div className="rounded-lg border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 overflow-hidden">
        <div className="flex items-center gap-2 px-4 py-3 border-b border-slate-200 dark:border-slate-800">
          <Activity className="h-4 w-4 text-slate-500" />
          <p className="text-[11px] uppercase tracking-[0.14em] text-slate-600 dark:text-slate-300">
            Recent calls
          </p>
        </div>
        <div className="overflow-x-auto" data-testid="ai-status-log">
          <table className="w-full text-left">
            <thead className="bg-slate-50 dark:bg-slate-950/60">
              <tr className="text-[11px] uppercase tracking-[0.12em] text-slate-500 dark:text-slate-400">
                <th className="px-3 py-2.5 font-medium">Status</th>
                <th className="px-3 py-2.5 font-medium">Purpose</th>
                <th className="px-3 py-2.5 font-medium">Attempt</th>
                <th className="px-3 py-2.5 font-medium">Latency</th>
                <th className="px-3 py-2.5 font-medium">When</th>
                <th className="px-3 py-2.5 font-medium">Detail</th>
              </tr>
            </thead>
            <tbody>
              {(status?.log || []).length === 0 ? (
                <tr>
                  <td
                    colSpan={6}
                    className="px-3 py-12 text-center text-sm text-slate-500 dark:text-slate-400"
                  >
                    No AI calls logged yet. Run a health check or generate a report.
                  </td>
                </tr>
              ) : (
                status.log.map((row, i) => <LogRow key={`${row.ts}-${i}`} row={row} />)
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
